import { Link } from 'react-router-dom';
import { Sparkles, Calendar, Image, TrendingUp, Zap, Globe, ArrowRight } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import AIMascot from '../components/AIMascot';
import FeatureVideoCard from '../components/FeatureVideoCard';

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Split Effect */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
        <div className={`container mx-auto px-6 py-4 transition-all duration-500 ${scrolled ? 'max-w-full' : 'max-w-7xl'}`}>
          <nav className={`flex items-center transition-all duration-500 ${scrolled ? 'justify-between' : 'flex-col gap-4'}`}>
            {/* Logo - centered initially, moves to left on scroll */}
            <div className={`flex items-center gap-2 transition-all duration-500 ${scrolled ? 'translate-x-0' : ''}`}>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center">
                <span className="text-white text-xl font-bold">S</span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Soail</h1>
            </div>

            {/* Navigation - centered initially, moves to right on scroll */}
            <div className={`hidden md:flex items-center gap-8 transition-all duration-500 ${scrolled ? 'translate-x-0' : ''}`}>
              <Link to="/dashboard/generators" className="text-gray-600 hover:text-gray-900 transition-colors">
                Use cases
              </Link>
              <a href="#" className="text-gray-600 hover:text-gray-900 transition-colors">
                Affiliate
              </a>
              <a href="#" className="text-gray-600 hover:text-gray-900 transition-colors">
                Blog
              </a>
              <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors">
                Login
              </Link>
              <Link
                to="/register"
                className="px-6 py-2.5 bg-white border-2 border-gray-900 text-gray-900 rounded-full font-medium hover:bg-gray-50 transition-all"
              >
                Buy now
              </Link>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-5xl text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 border border-purple-200 rounded-full mb-8">
            <span className="text-purple-600 text-sm font-medium">AI for Marketing</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-gray-900 mb-8 leading-tight">
            Launch 10x more
            <br />
            content. 75% faster.
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
            Soail turns your website into ads, emails, and social posts. 100's of content pieces generated, while you sleep.
          </p>

          {/* CTA Button */}
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Buy now
            <ArrowRight className="w-5 h-5" />
          </Link>

          {/* Trust Badges */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 mt-16">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <span className="text-white text-xl font-bold">F</span>
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-900">Backed by VC funds</p>
                <p className="text-sm text-gray-500">$5MM valuation</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-900">Powered by OpenAI</p>
                <p className="text-sm text-gray-500">Leading AI model</p>
              </div>
            </div>
          </div>

          {/* AI Mascot */}
          <div className="flex justify-center mt-20">
            <AIMascot className="w-64 h-64 md:w-80 md:h-80" />
          </div>
        </div>
      </section>

      {/* Brand Showcase Slider - "You've probably seen our work" */}
      <BrandShowcaseSlider />

      {/* "At Soail we're revolutionizing" Section */}
      <section className="py-32 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="space-y-12">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
              At Soail we're revolutionizing
              <br />
              how founders create{' '}
              <AnimatedIcon icons={['💡', '✨', '🚀', '⚡']} />{' '}
              content.
            </h2>

            <p className="text-3xl md:text-4xl text-gray-800 leading-relaxed">
              Using the latest AI models we
              <br />
              capture{' '}
              <AnimatedIcon icons={['🧠', '🎯', '💭', '🎨']} />{' '}
              your brand's
              <br />
              essence to deliver agency-like
              <br />
              content across all{' '}
              <PlatformIcons />{' '}
              platforms.
            </p>

            <p className="text-3xl md:text-4xl text-gray-800 leading-relaxed">
              So you can do the work of a
              <br />
              full team{' '}
              <AnimatedIcon icons={['👥', '🤝', '👨‍💼', '👩‍💼']} />{' '}
              without hiring one.
            </p>
          </div>
        </div>
      </section>

      {/* The All-in-One Marketing Tool Section with 3D Wheel Slider */}
      <section className="py-20 px-6 bg-white overflow-hidden">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-6">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              The All-in-One Marketing Tool
            </h2>
            <p className="text-lg md:text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
              <span className="italic">Skip the tools, templates, and tabs.</span> This is your AI for marketing, built to scale. From ads, emails and social posts. Core marketing areas? Covered.
            </p>
          </div>

          {/* 3D Wheel Carousel */}
          <WheelCarousel />
        </div>
      </section>

      {/* Mascot with Magic Wand and Feature Video Card */}
      <section className="relative py-20 overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="container mx-auto max-w-6xl px-6">
          {/* Section heading */}
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              See Soail in Action
            </h2>
            <p className="text-lg text-gray-600">
              Watch how AI transforms your marketing workflow
            </p>
          </div>

          {/* Magic Wand Mascot */}
          <div className="flex justify-center mb-8">
            <AIMascot className="w-64 h-64 md:w-72 md:h-72" showRatings={false} />
          </div>

          {/* 3D Feature Video Card */}
          <FeatureVideoCard className="mt-0" />

          {/* Scroll hint */}
          <div className="text-center mt-12">
            <p className="text-gray-500 text-sm">Scroll to watch the magic unfold</p>
            <div className="mt-2 animate-bounce">
              <svg className="w-6 h-6 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Everything you need to scale content
            </h2>
            <p className="text-xl text-gray-600">
              Powerful AI generators for every marketing channel
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Sparkles className="w-8 h-8" />}
              title="7 AI Generators"
              description="Facebook ads, newsletters, Instagram ads, UGC, TikTok videos, and more."
              gradient="from-blue-500 to-cyan-500"
            />
            <FeatureCard
              icon={<Calendar className="w-8 h-8" />}
              title="Smart Scheduling"
              description="Auto-post to Instagram, Facebook, TikTok, and LinkedIn on autopilot."
              gradient="from-purple-500 to-pink-500"
            />
            <FeatureCard
              icon={<Globe className="w-8 h-8" />}
              title="Multi-Platform"
              description="One dashboard to manage all your social media accounts and content."
              gradient="from-pink-500 to-orange-500"
            />
            <FeatureCard
              icon={<Image className="w-8 h-8" />}
              title="AI Image Generation"
              description="Create stunning visuals with DALL-E. Automatically branded with your logo."
              gradient="from-green-500 to-emerald-500"
            />
            <FeatureCard
              icon={<TrendingUp className="w-8 h-8" />}
              title="Analytics Dashboard"
              description="Track performance across all platforms with real-time insights."
              gradient="from-orange-500 to-red-500"
            />
            <FeatureCard
              icon={<Zap className="w-8 h-8" />}
              title="Full Automation"
              description="Let AI handle everything. Wake up to fresh content every day."
              gradient="from-indigo-500 to-purple-500"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Ready to 10x your content?
          </h2>
          <p className="text-xl text-gray-300 mb-12">
            Join thousands of marketers using AI to scale their content production.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-10 py-5 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white rounded-full font-bold text-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Get Started Free
            <ArrowRight className="w-6 h-6" />
          </Link>
          <p className="text-gray-400 mt-6 text-sm">
            No credit card required • Free forever plan available
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-white border-t border-gray-100">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500"></div>
              <span className="text-lg font-bold text-gray-900">Soail</span>
            </div>
            <div className="flex gap-6 text-sm text-gray-600">
              <a href="#" className="hover:text-gray-900">Privacy</a>
              <a href="#" className="hover:text-gray-900">Terms</a>
              <a href="#" className="hover:text-gray-900">Contact</a>
            </div>
            <p className="text-sm text-gray-500">
              © 2025 Soail. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
  gradient,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
}) {
  return (
    <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradient} text-white flex items-center justify-center mb-6`}>
        {icon}
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
}

function ExampleCard({
  brand,
  tagline,
  product,
  description,
  gradient,
  darkText = true,
}: {
  brand: string;
  tagline?: string;
  product?: string;
  description?: string;
  gradient: string;
  darkText?: boolean;
}) {
  return (
    <div className="flex-none w-80 snap-start">
      <div className={`relative h-96 rounded-3xl bg-gradient-to-br ${gradient} overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105`}>
        <div className={`absolute inset-0 p-8 flex flex-col justify-between ${darkText ? 'text-gray-900' : 'text-white'}`}>
          <div>
            {tagline && (
              <p className={`text-sm mb-2 ${darkText ? 'text-gray-700' : 'text-white/90'}`}>
                {tagline}
              </p>
            )}
            {product && (
              <h3 className="text-2xl font-bold mb-2">{product}</h3>
            )}
            {description && (
              <p className={`text-sm leading-relaxed ${darkText ? 'text-gray-700' : 'text-white/90'}`}>
                {description}
              </p>
            )}
          </div>
          <div>
            <p className="text-3xl font-bold">{brand}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnimatedIcon({ icons }: { icons: string[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % icons.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [icons.length]);

  return (
    <span className="inline-block transition-all duration-500 animate-pulse">
      {icons[currentIndex]}
    </span>
  );
}

function PlatformIcons() {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % 5);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const platforms = [
    {
      name: 'LinkedIn',
      icon: (
        <svg className="w-8 h-8" fill="#0077B5" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
    },
    {
      name: 'Instagram',
      icon: (
        <svg className="w-8 h-8" fill="url(#instagram-gradient)" viewBox="0 0 24 24">
          <defs>
            <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FD5949" />
              <stop offset="50%" stopColor="#D6249F" />
              <stop offset="100%" stopColor="#285AEB" />
            </linearGradient>
          </defs>
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
        </svg>
      ),
    },
    {
      name: 'Facebook',
      icon: (
        <svg className="w-8 h-8" fill="#1877F2" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
    },
    {
      name: 'TikTok',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
        </svg>
      ),
    },
    {
      name: 'Twitter/X',
      icon: (
        <svg className="w-8 h-8" fill="#000000" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
    },
  ];

  return (
    <span className="inline-flex items-center gap-2 transition-all duration-500">
      {platforms[currentIndex].icon}
    </span>
  );
}

function ThreeDCardShowcase() {
  const [activeTab, setActiveTab] = useState(0);

  const categories = [
    {
      name: 'Videos',
      items: [
        {
          title: 'UGC Creator Video',
          image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400&h=600&fit=crop',
          isVideo: true,
        },
        {
          title: 'Product Demo',
          image: 'https://images.unsplash.com/photo-1598550476439-6847785fcea6?w=400&h=600&fit=crop',
          isVideo: true,
        },
        {
          title: 'Testimonial Video',
          image: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?w=400&h=600&fit=crop',
          isVideo: true,
        },
      ],
    },
    {
      name: 'Ads',
      items: [
        {
          title: 'Facebook Ad',
          image: 'https://images.unsplash.com/photo-1600132806608-231446b2e7af?w=400&h=600&fit=crop',
        },
        {
          title: 'Instagram Story',
          image: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?w=400&h=600&fit=crop',
        },
        {
          title: 'Google Display Ad',
          image: 'https://images.unsplash.com/photo-1607083206968-13611e3d76db?w=400&h=600&fit=crop',
        },
      ],
    },
    {
      name: 'Socials',
      items: [
        {
          title: 'Instagram Post',
          image: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=400&h=600&fit=crop',
        },
        {
          title: 'TikTok Video',
          image: 'https://images.unsplash.com/photo-1611162618479-ee3d24aaef0b?w=400&h=600&fit=crop',
        },
        {
          title: 'LinkedIn Carousel',
          image: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=400&h=600&fit=crop',
        },
      ],
    },
    {
      name: 'Emails',
      items: [
        {
          title: 'Newsletter',
          image: 'https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=400&h=600&fit=crop',
        },
        {
          title: 'Promotional Email',
          image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=400&h=600&fit=crop',
        },
        {
          title: 'Welcome Series',
          image: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?w=400&h=600&fit=crop',
        },
      ],
    },
  ];

  return (
    <div className="space-y-8">
      {/* Tab Navigation */}
      <div className="flex justify-center gap-2 flex-wrap">
        {categories.map((category, index) => (
          <button
            key={category.name}
            onClick={() => setActiveTab(index)}
            className={`px-8 py-3 rounded-full font-semibold text-lg transition-all duration-300 ${
              activeTab === index
                ? 'bg-gray-900 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-100 border-2 border-gray-200'
            }`}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* 3D Cards Display */}
      <div className="relative h-[600px] flex items-center justify-center perspective-1000">
        <div className="flex gap-8 items-center justify-center">
          {categories[activeTab].items.map((item, index) => (
            <div
              key={index}
              className={`transition-all duration-700 ${
                index === 0
                  ? 'z-10 scale-100 opacity-100'
                  : index === 1
                  ? 'z-20 scale-110 opacity-100 shadow-2xl'
                  : 'z-10 scale-100 opacity-100'
              }`}
              style={{
                transform:
                  index === 0
                    ? 'translateX(50px) rotateY(15deg)'
                    : index === 2
                    ? 'translateX(-50px) rotateY(-15deg)'
                    : 'translateX(0) rotateY(0deg)',
              }}
            >
              <div className="relative w-80 h-[500px] rounded-3xl overflow-hidden shadow-2xl hover:scale-105 transition-transform duration-300">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-white text-2xl font-bold">{item.title}</h3>
                  {'isVideo' in item && item.isVideo && (
                    <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                      <span className="text-white text-sm">Video</span>
                    </div>
                  )}
                </div>
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BrandShowcaseSlider() {
  const [scrollX, setScrollX] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Brand showcase cards with GIF/video placeholders
  const brandCards = [
    {
      id: 1,
      brand: 'hey bud',
      tagline: 'Skincare, naturally.',
      gradient: 'from-lime-300 to-green-400',
      videoPlaceholder: 'https://images.unsplash.com/photo-1556228578-8c89e6adf883?w=400&h=500&fit=crop',
    },
    {
      id: 2,
      brand: 'Glossier',
      tagline: 'Skin first. Makeup second.',
      gradient: 'from-pink-200 to-pink-400',
      videoPlaceholder: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=400&h=500&fit=crop',
    },
    {
      id: 3,
      brand: 'HERMÈS',
      tagline: 'Luxury redefined.',
      gradient: 'from-orange-300 to-orange-500',
      videoPlaceholder: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=500&fit=crop',
    },
    {
      id: 4,
      brand: 'UGC Creator',
      tagline: 'Real people, real content.',
      gradient: 'from-purple-300 to-purple-500',
      videoPlaceholder: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=500&fit=crop',
    },
    {
      id: 5,
      brand: 'Oatly',
      tagline: 'It\'s like milk, but for humans.',
      gradient: 'from-blue-200 to-blue-400',
      videoPlaceholder: 'https://images.unsplash.com/photo-1523473827533-2a64d0d36748?w=400&h=500&fit=crop',
    },
    {
      id: 6,
      brand: 'Allbirds',
      tagline: 'The world\'s most comfortable shoes.',
      gradient: 'from-emerald-200 to-emerald-400',
      videoPlaceholder: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=500&fit=crop',
    },
    {
      id: 7,
      brand: 'Aesop',
      tagline: 'Seek wisdom.',
      gradient: 'from-amber-200 to-amber-400',
      videoPlaceholder: 'https://images.unsplash.com/photo-1571781926291-c477ebfd024b?w=400&h=500&fit=crop',
    },
    {
      id: 8,
      brand: 'Warby Parker',
      tagline: 'Eyewear with a purpose.',
      gradient: 'from-cyan-200 to-cyan-400',
      videoPlaceholder: 'https://images.unsplash.com/photo-1574258495973-f010dfbb5371?w=400&h=500&fit=crop',
    },
  ];

  // Duplicate cards for infinite scroll effect
  const duplicatedCards = [...brandCards, ...brandCards, ...brandCards];
  const cardWidth = 320; // Card width + gap
  const totalWidth = brandCards.length * cardWidth;

  // Continuous right-to-left animation - faster speed
  useEffect(() => {
    const animate = () => {
      setScrollX((prev) => {
        const newX = prev - 1.5; // Faster speed
        // Reset when we've scrolled one full set
        if (Math.abs(newX) >= totalWidth) {
          return 0;
        }
        return newX;
      });
    };
    const interval = setInterval(animate, 16); // Smoother 60fps
    return () => clearInterval(interval);
  }, [totalWidth]);

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
      <div className="container mx-auto max-w-6xl text-center mb-12 px-6">
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
          You've probably seen our work.
        </h2>
        <p className="text-2xl md:text-3xl text-gray-600">
          You just didn't know it was AI.
        </p>
      </div>

      {/* Slider Container - full width, no padding */}
      <div className="w-full overflow-hidden" ref={containerRef}>
        {/* Cards Track */}
        <div
          className="flex gap-5 py-4"
          style={{
            transform: `translateX(${scrollX}px)`,
            width: 'max-content',
          }}
        >
          {duplicatedCards.map((card, index) => (
            <div
              key={`${card.id}-${index}`}
              className="flex-none w-[280px] group cursor-pointer"
            >
              <div className="relative h-[380px] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105">
                {/* Full card image - no border */}
                <img
                  src={card.videoPlaceholder}
                  alt={card.brand}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />

                {/* Brand info overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
                  <p className="text-white/80 text-sm mb-1">{card.tagline}</p>
                  <h3 className="text-white text-xl font-bold">{card.brand}</h3>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation dots/indicator */}
      <div className="flex justify-center gap-2 mt-8">
        {brandCards.slice(0, 4).map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === 0 ? 'bg-gray-800 w-6' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </section>
  );
}

function WheelCarousel() {
  const [activeTab, setActiveTab] = useState<'Videos' | 'Ads' | 'Socials' | 'Emails'>('Videos');
  const [rotation, setRotation] = useState(0);

  const tabs = ['Videos', 'Ads', 'Socials', 'Emails'] as const;

  // 10 UNIQUE cards per category
  const cardData = {
    Videos: [
      { id: 1, src: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=300&h=450&fit=crop' },
      { id: 2, src: 'https://images.unsplash.com/photo-1585399000684-d2f72660f092?w=300&h=450&fit=crop' },
      { id: 3, src: 'https://images.unsplash.com/photo-1526947425960-945c6e72858f?w=300&h=450&fit=crop' },
      { id: 4, src: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=300&h=450&fit=crop' },
      { id: 5, src: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=300&h=450&fit=crop' },
      { id: 6, src: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=300&h=450&fit=crop' },
      { id: 7, src: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&h=450&fit=crop' },
      { id: 8, src: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=450&fit=crop' },
      { id: 9, src: 'https://images.unsplash.com/photo-1583394838336-acd977736f90?w=300&h=450&fit=crop' },
      { id: 10, src: 'https://images.unsplash.com/photo-1560343090-f0409e92791a?w=300&h=450&fit=crop' },
    ],
    Ads: [
      { id: 1, src: 'https://images.unsplash.com/photo-1556228578-8c89e6adf883?w=300&h=450&fit=crop' },
      { id: 2, src: 'https://images.unsplash.com/photo-1571781926291-c477ebfd024b?w=300&h=450&fit=crop' },
      { id: 3, src: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=300&h=450&fit=crop' },
      { id: 4, src: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=300&h=450&fit=crop' },
      { id: 5, src: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=300&h=450&fit=crop' },
      { id: 6, src: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=300&h=450&fit=crop' },
      { id: 7, src: 'https://images.unsplash.com/photo-1445205170230-053b83016050?w=300&h=450&fit=crop' },
      { id: 8, src: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=450&fit=crop' },
      { id: 9, src: 'https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=300&h=450&fit=crop' },
      { id: 10, src: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=300&h=450&fit=crop' },
    ],
    Socials: [
      { id: 1, src: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&h=450&fit=crop' },
      { id: 2, src: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=300&h=450&fit=crop' },
      { id: 3, src: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=300&h=450&fit=crop' },
      { id: 4, src: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=300&h=450&fit=crop' },
      { id: 5, src: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=450&fit=crop' },
      { id: 6, src: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&h=450&fit=crop' },
      { id: 7, src: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=300&h=450&fit=crop' },
      { id: 8, src: 'https://images.unsplash.com/photo-1488161628813-04466f872be2?w=300&h=450&fit=crop' },
      { id: 9, src: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=300&h=450&fit=crop' },
      { id: 10, src: 'https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?w=300&h=450&fit=crop' },
    ],
    Emails: [
      { id: 1, src: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=300&h=450&fit=crop' },
      { id: 2, src: 'https://images.unsplash.com/photo-1557200134-90327ee9fafa?w=300&h=450&fit=crop' },
      { id: 3, src: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=300&h=450&fit=crop' },
      { id: 4, src: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=300&h=450&fit=crop' },
      { id: 5, src: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=300&h=450&fit=crop' },
      { id: 6, src: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=450&fit=crop' },
      { id: 7, src: 'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=300&h=450&fit=crop' },
      { id: 8, src: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=300&h=450&fit=crop' },
      { id: 9, src: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=300&h=450&fit=crop' },
      { id: 10, src: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=300&h=450&fit=crop' },
    ],
  };

  const items = cardData[activeTab];
  const cardCount = items.length;
  const anglePerCard = 360 / cardCount; // Each card gets equal slice of the cylinder
  const radius = 500; // Radius of the cylinder

  // Continuous rotation - right to left (negative direction)
  useEffect(() => {
    const animate = () => {
      setRotation(prev => prev - 0.3); // Negative for right-to-left
    };
    const interval = setInterval(animate, 20);
    return () => clearInterval(interval);
  }, []);

  // Calculate 3D cylinder position for each card
  const getCardStyle = (index: number) => {
    // Each card's angle on the cylinder
    const cardAngle = (index * anglePerCard) + rotation;
    const radians = (cardAngle * Math.PI) / 180;

    // Calculate X and Z positions on cylinder surface
    const x = Math.sin(radians) * radius;
    const z = Math.cos(radians) * radius - radius; // Offset so front is at z=0

    // Cards facing away from viewer should be hidden
    const isFacingAway = Math.cos(radians) < -0.3;

    return {
      transform: `translateX(${x}px) translateZ(${z}px) rotateY(${cardAngle}deg)`,
      opacity: isFacingAway ? 0 : 1,
      zIndex: Math.round(z + radius),
    };
  };

  return (
    <div className="w-full relative">
      {/* 3D Cylinder Wheel Carousel */}
      <div
        className="relative h-[400px] mb-10 overflow-hidden"
        style={{ perspective: '1000px' }}
      >
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ transformStyle: 'preserve-3d' }}
        >
          {items.map((card, index) => (
            <div
              key={card.id}
              className="absolute w-[180px] h-[280px] rounded-[32px] overflow-hidden shadow-2xl"
              style={{
                ...getCardStyle(index),
                backfaceVisibility: 'hidden',
                transition: 'opacity 0.3s ease',
              }}
            >
              <img
                src={card.src}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>

        {/* Gradient fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-40 bg-gradient-to-r from-white to-transparent z-30 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-40 bg-gradient-to-l from-white to-transparent z-30 pointer-events-none" />
      </div>

      {/* Tab Buttons - BELOW the wheel */}
      <div className="flex justify-center gap-8">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setRotation(0);
            }}
            className="relative pb-2 text-base font-medium transition-all duration-300"
          >
            <span className={activeTab === tab ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}>
              {tab}
            </span>
            <div
              className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full transition-all duration-300 ${
                activeTab === tab ? 'bg-orange-500' : 'bg-transparent'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

